import { ConditionallyRenderedExperienceHook } from 'app/src/components/ConditionallyRenderedExperienceHook';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { ApolloError } from '@apollo/client';
import { isDesktop } from '@trello/browser';
import { useMigrationWizardQuery } from './MigrationWizardQuery.generated';
import { DismissMessageKeys } from './constants';
import { MigrationWizardExperience } from './types';
import { useMigratedBoards } from './useMigratedBoards';
import { sendErrorEvent } from '@trello/error-reporting';
import { useFeatureFlag } from '@trello/feature-flag-client';
import Cookies from 'js-cookie';
import { isActive } from '@trello/paid-account';

const reportError = (error: ApolloError | undefined) => {
  if (error) {
    sendErrorEvent(error, {
      tags: {
        ownershipArea: 'trello-mocha',
        feature: 'Migration Wizard',
      },
      extraData: {
        hook: 'useMigrationWizard',
      },
    });
  }
};

export const useMigrationWizardBanner: ConditionallyRenderedExperienceHook<MigrationWizardExperience> = (
  options,
) => {
  const isMigrationWizardFlagEnabled = useFeatureFlag(
    'btg.migration-wizard',
    false,
  );
  const isVeryHeavyUsageFlagEnabled = useFeatureFlag(
    'btg.migration-wizard.50-plus',
    false,
  );

  const [experience, setExperience] = useState<MigrationWizardExperience>(
    MigrationWizardExperience.None,
  );
  const [
    shouldRecheckExperience,
    setShouldRecheckExperience,
  ] = useState<boolean>(false);

  const { data, loading, error } = useMigrationWizardQuery({
    skip: !isMigrationWizardFlagEnabled || options?.skip,
  });

  useEffect(() => {
    reportError(error);
  }, [error]);

  const member = data?.member;
  const confirmed = Boolean(member?.confirmed);
  const oneTimeMessagesDismissed = useMemo(
    () => data?.member?.oneTimeMessagesDismissed ?? [],
    [data?.member?.oneTimeMessagesDismissed],
  );

  const simulation = Cookies.get('teamify') || '{}';
  const simulatedTeamify = useMemo(() => {
    try {
      const val = JSON.parse(simulation);
      return {
        ...member?.teamify,
        ...val,
      };
    } catch (err) {
      return null;
    }
  }, [simulation, member?.teamify]);

  // teamify properties
  const teamify = simulatedTeamify || member?.teamify;
  const state = teamify?.state;
  const impact = teamify?.impact;
  const voluntaryDone = teamify?.voluntaryDone;
  const collaborativeTeamlessBoards = teamify?.collaborativeTeamlessBoards ?? 0;
  const soloTeamlessBoards = teamify?.soloTeamlessBoards ?? 0;
  const idOrgCreated = teamify?.idOrgCreated;
  const idOrgSelected = teamify?.idOrgSelected;

  // Sunsetting Trello Gold logic
  const paidAccount = data?.member?.paidAccount;
  const goldIsActive = isActive({
    products: paidAccount?.products ?? [],
    standing: paidAccount?.standing ?? -1,
  });

  const isQualifiedGoldUser = goldIsActive;

  const {
    migratedBoards,
    loading: loadingMigratedBoards,
    error: migratedBoardsError,
  } = useMigratedBoards({
    idOrg: idOrgCreated || idOrgSelected || '',
  });

  useEffect(() => {
    reportError(migratedBoardsError);
  }, [migratedBoardsError]);

  const hasDismissedPostMigrationBanner = oneTimeMessagesDismissed.includes(
    DismissMessageKeys.TidyUp,
  );

  const getExperience = useCallback(() => {
    let result = MigrationWizardExperience.None;

    if (state === 'nope' || !confirmed) {
      result = MigrationWizardExperience.None;
    } else if (state === 'pending') {
      if (
        isQualifiedGoldUser &&
        collaborativeTeamlessBoards + soloTeamlessBoards > 0
      ) {
        result = MigrationWizardExperience.GoldUserWithTeamlessBoards;
      } else if (collaborativeTeamlessBoards + soloTeamlessBoards >= 50) {
        if (isVeryHeavyUsageFlagEnabled) {
          result = MigrationWizardExperience.VeryHeavyUsage;
        } else {
          result = MigrationWizardExperience.None;
        }
      } else if (isMigrationWizardFlagEnabled) {
        if (collaborativeTeamlessBoards <= 0 && soloTeamlessBoards <= 0) {
          result = MigrationWizardExperience.None;
        } else if (isDesktop()) {
          result = MigrationWizardExperience.Desktop;
        } else if (voluntaryDone) {
          result = MigrationWizardExperience.Advanced;
        } else {
          result = MigrationWizardExperience.Pre;
        }
      } else {
        result = MigrationWizardExperience.None;
      }
    } else if (state === 'done') {
      if (migratedBoards.length >= 50) {
        result = MigrationWizardExperience.SuccessVeryHeavyUsage;
      } else {
        if (isMigrationWizardFlagEnabled) {
          if (
            hasDismissedPostMigrationBanner ||
            impact === 'none' ||
            (!idOrgCreated && !idOrgSelected) ||
            migratedBoards.length <= 0
          ) {
            result = MigrationWizardExperience.None;
          } else if (isDesktop()) {
            result = MigrationWizardExperience.Desktop;
          } else {
            result = MigrationWizardExperience.Post;
          }
        } else {
          result = MigrationWizardExperience.None;
        }
      }
    }

    return result;
  }, [
    collaborativeTeamlessBoards,
    confirmed,
    hasDismissedPostMigrationBanner,
    idOrgCreated,
    idOrgSelected,
    impact,
    isMigrationWizardFlagEnabled,
    isQualifiedGoldUser,
    isVeryHeavyUsageFlagEnabled,
    migratedBoards.length,
    soloTeamlessBoards,
    state,
    voluntaryDone,
  ]);

  useEffect(() => {
    // If the user is not seeing any of our banners, continuosly check if they
    // have become eligible for one.  Also recheck when we force it; this is
    // used mainly when closing the dialog, to check if we should also hide the
    // banner.
    if (
      !loading &&
      !loadingMigratedBoards &&
      (experience === MigrationWizardExperience.None || shouldRecheckExperience)
    ) {
      // conditionally set the state variable to avoid infinite renders
      const nextExperience = getExperience();
      if (nextExperience !== experience) {
        setExperience(nextExperience);
      }

      if (shouldRecheckExperience) {
        setShouldRecheckExperience(false);
      }
    }
  }, [
    experience,
    getExperience,
    loading,
    loadingMigratedBoards,
    setExperience,
    setShouldRecheckExperience,
    shouldRecheckExperience,
  ]);

  const showExperience = experience !== MigrationWizardExperience.None;

  return {
    wouldRender: !error && !loading && !loadingMigratedBoards && showExperience,
    experience,
  };
};
